import React, { useState, useEffect, useCallback } from 'react';
import { Plus, CheckCircle, Loader, Pencil, X, AlertTriangle } from 'lucide-react';
import API from '../api';
import { GRADI } from '../utils/statiRicorso';

// ─────────────────────────────────────────────────────────────────────────────
// Tipi ricorso — Configurazione
// Oltre a nome e descrizione, ogni tipo porta i segmenti con cui l'app compone
// il codice ricorso (schema Esolver RIC001): Tipo (varianti RICORS, es. IND/APP)
// e Titoli (varianti TITOL, es. PDC/OTT/RUO). Senza i due segmenti il codice
// non si genera: le righe incomplete sono evidenziate.
// ─────────────────────────────────────────────────────────────────────────────

const LabelInput = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 11, color: '#718096', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    {children}
  </div>
);

const VUOTO = { nome: '', descrizione: '', seg_tipo: '', seg_titoli: '', conta_come_adesione: false, grado: 'primo_grado' };
const etichettaGrado = (g) => (GRADI.find(([k]) => k === g) || [null, g || '—'])[1];

function SelectVariante({ value, onChange, opzioni, placeholder }) {
  return (
    <select className="input-filtro" style={{ width: '100%' }} value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {opzioni.map(v => (
        <option key={v.codice} value={v.codice}>{v.codice} — {v.descrizione}</option>
      ))}
    </select>
  );
}

function Segmento({ codice, descrizione }) {
  if (!codice) return <span style={{ color: '#C05621', fontSize: 12 }}>—</span>;
  return (
    <span title={descrizione || codice}
      style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, padding: '2px 7px', borderRadius: 5,
        background: '#EBF4FF', color: '#2B6CB0', border: '1px solid #BEE3F8' }}>
      {codice}
    </span>
  );
}

export default function TipiRicorso() {
  const [tipi, setTipi] = useState([]);
  const [varianti, setVarianti] = useState({ RICORS: [], TITOL: [] });
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState(null);
  const [avviso, setAvviso] = useState(null);
  const [mostraNuovo, setMostraNuovo] = useState(false);
  const [nuovo, setNuovo] = useState(VUOTO);
  const [salvando, setSalvando] = useState(false);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(VUOTO);

  const carica = useCallback(() => {
    setCaricamento(true);
    API.get('/finanziario/tipi-ricorso')
      .then(r => setTipi(r.data))
      .catch(() => setErrore('Errore nel caricamento dei tipi ricorso.'))
      .finally(() => setCaricamento(false));
  }, []);

  useEffect(() => {
    carica();
    API.get('/finanziario/varianti-codice')
      .then(r => {
        const g = { RICORS: [], TITOL: [] };
        (r.data || []).forEach(v => { if (g[v.tipo_variante]) g[v.tipo_variante].push(v); });
        setVarianti(g);
      })
      .catch(() => {});
  }, [carica]);

  const descr = (tipoVariante, codice) =>
    (varianti[tipoVariante] || []).find(v => v.codice === codice)?.descrizione;

  const incompleti = tipi.filter(t => t.attivo !== false && (!t.seg_tipo || !t.seg_titoli));

  const normalizza = (d) => ({
    nome: d.nome.trim(),
    descrizione: d.descrizione.trim() || null,
    seg_tipo: d.seg_tipo || null,
    seg_titoli: d.seg_titoli || null,
    conta_come_adesione: !!d.conta_come_adesione,
    grado: d.grado || 'primo_grado',
  });

  const crea = async () => {
    const d = normalizza(nuovo);
    if (!d.nome) { setErrore('Inserisci il nome del tipo.'); return; }
    setSalvando(true); setErrore(null); setAvviso(null);
    try {
      const r = await API.post('/finanziario/tipi-ricorso', d);
      if (r.data.creato) {
        setAvviso(`Tipo "${r.data.nome}" creato.` + (!d.seg_tipo || !d.seg_titoli ? ' Ricorda di completare Tipo e Titoli per generare il codice.' : ''));
      } else {
        setAvviso(`Il tipo "${r.data.nome}" esisteva già.`);
      }
      setNuovo(VUOTO); setMostraNuovo(false);
      carica();
    } catch (e) {
      setErrore(e.response?.data?.detail || 'Errore nella creazione.');
    } finally {
      setSalvando(false);
    }
  };

  const salvaModifica = async (id) => {
    const d = normalizza(edit);
    if (!d.nome) { setErrore('Il nome non può essere vuoto.'); return; }
    setSalvando(true); setErrore(null); setAvviso(null);
    try {
      await API.patch(`/finanziario/tipi-ricorso/${id}`, d);
      setAvviso('Tipo aggiornato.');
      setEditId(null);
      carica();
    } catch (e) {
      setErrore(e.response?.data?.detail || 'Errore nel salvataggio.');
    } finally {
      setSalvando(false);
    }
  };

  const toggleAdesione = async (t) => {
    setErrore(null); setAvviso(null);
    try {
      await API.patch(`/finanziario/tipi-ricorso/${t.id}`, { conta_come_adesione: !t.conta_come_adesione });
      carica();
    } catch (e) {
      setErrore(e.response?.data?.detail || 'Errore nel salvataggio.');
    }
  };

  // Solo i tipi che contano come adesione permettono l'origine "Nuova adesione" sulla scheda (report Campagne)
  const casellaAdesione = (dati, setDati) => (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4A5568',
      marginBottom: 12, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!dati.conta_come_adesione}
        onChange={e => setDati({ ...dati, conta_come_adesione: e.target.checked })} />
      Conta come adesione (ricorso a cui i clienti aderiscono)
    </label>
  );

  const toggleAttivo = async (t) => {
    setErrore(null); setAvviso(null);
    try {
      await API.patch(`/finanziario/tipi-ricorso/${t.id}`, { attivo: !t.attivo });
      carica();
    } catch (e) {
      setErrore(e.response?.data?.detail || 'Errore nel cambio stato.');
    }
  };

  // grado: filtra i tipi nel form ricorso (migrazione 026)
  const campiSegmenti = (dati, setDati) => (
    <>
      <LabelInput label="Grado">
        <select className="input-filtro" style={{ width: '100%' }} aria-label="Grado del tipo" value={dati.grado || 'primo_grado'}
          onChange={e => setDati({ ...dati, grado: e.target.value })}>
          {GRADI.map(([g, t]) => <option key={g} value={g}>{t}</option>)}
        </select>
      </LabelInput>
      <LabelInput label="Tipo (codice)">
        <SelectVariante value={dati.seg_tipo} onChange={v => setDati({ ...dati, seg_tipo: v })}
          opzioni={varianti.RICORS} placeholder="Seleziona…" />
      </LabelInput>
      <LabelInput label="Titoli (codice)">
        <SelectVariante value={dati.seg_titoli} onChange={v => setDati({ ...dati, seg_titoli: v })}
          opzioni={varianti.TITOL} placeholder="Seleziona…" />
      </LabelInput>
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, margin: 0 }}>Tipi ricorso</h1>
        {!mostraNuovo && (
          <button className="btn-cerca" onClick={() => { setMostraNuovo(true); setAvviso(null); setEditId(null); }}>
            <Plus size={16} /> Nuovo tipo
          </button>
        )}
      </div>

      {errore && (
        <div style={{ fontSize: 13, color: '#C53030', background: '#FCECEA', padding: '8px 12px',
          borderRadius: 8, marginBottom: 12 }}>{errore}</div>
      )}
      {avviso && (
        <div style={{ fontSize: 13, color: '#276749', background: '#EBF9F1', padding: '8px 12px',
          borderRadius: 8, marginBottom: 12 }}>{avviso}</div>
      )}
      {!caricamento && incompleti.length > 0 && (
        <div style={{ fontSize: 13, color: '#9C4221', background: '#FFFAF0', border: '1px solid #FBD38D',
          padding: '8px 12px', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} />
          {incompleti.length === 1 ? '1 tipo attivo non ha' : `${incompleti.length} tipi attivi non hanno`} i segmenti
          Tipo/Titoli: per {incompleti.length === 1 ? 'questo tipo' : 'questi tipi'} l'app non può generare il codice ricorso.
        </div>
      )}

      {mostraNuovo && (
        <div style={{ background: '#F7FDFB', border: '1px solid #C6EFE3', borderRadius: 10,
          padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.7fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <LabelInput label="Nome tipo *">
              <input className="input-filtro" style={{ width: '100%' }} autoFocus
                value={nuovo.nome} onChange={e => setNuovo({ ...nuovo, nome: e.target.value })}
                placeholder="es. Concorso docenti 2026" />
            </LabelInput>
            <LabelInput label="Descrizione">
              <input className="input-filtro" style={{ width: '100%' }}
                value={nuovo.descrizione} onChange={e => setNuovo({ ...nuovo, descrizione: e.target.value })}
                placeholder="Descrizione facoltativa..." />
            </LabelInput>
            {campiSegmenti(nuovo, setNuovo)}
          </div>
          {casellaAdesione(nuovo, setNuovo)}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-cerca" onClick={crea} disabled={salvando}>
              {salvando ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />} Crea tipo
            </button>
            <button onClick={() => { setMostraNuovo(false); setNuovo(VUOTO); setErrore(null); }}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #dde1e7',
                background: 'white', cursor: 'pointer', color: '#718096' }}>Annulla</button>
          </div>
        </div>
      )}

      {caricamento ? (
        <div style={{ color: '#a0aec0', padding: 24 }}><Loader size={18} className="spin" /> Caricamento…</div>
      ) : (
        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #edf0f4', overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 12, padding: '8px 16px', background: '#F7F8FA', fontSize: 11,
            color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #edf0f4' }}>
            <div style={{ flex: 1 }}>Nome</div>
            <div style={{ flex: 1.6 }}>Descrizione</div>
            <div style={{ width: 110 }}>Grado</div>
            <div style={{ width: 70, textAlign: 'center' }}>Tipo</div>
            <div style={{ width: 70, textAlign: 'center' }}>Titoli</div>
            <div style={{ width: 80, textAlign: 'center' }}>Adesione</div>
            <div style={{ width: 190 }} />
          </div>
          {tipi.map((t, i) => {
            const incompleto = t.attivo !== false && (!t.seg_tipo || !t.seg_titoli);
            return (
              <div key={t.id} style={{ padding: '12px 16px', borderTop: i ? '1px solid #f2f4f8' : 'none',
                opacity: t.attivo === false ? 0.5 : 1, background: incompleto && editId !== t.id ? '#FFFAF0' : 'white' }}>
                {editId === t.id ? (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.7fr 1fr 1fr 1fr', gap: 12, marginBottom: 10 }}>
                      <LabelInput label="Nome *">
                        <input className="input-filtro" style={{ width: '100%' }} value={edit.nome}
                          onChange={e => setEdit({ ...edit, nome: e.target.value })} />
                      </LabelInput>
                      <LabelInput label="Descrizione">
                        <input className="input-filtro" style={{ width: '100%' }} value={edit.descrizione}
                          onChange={e => setEdit({ ...edit, descrizione: e.target.value })} placeholder="Descrizione…" />
                      </LabelInput>
                      {campiSegmenti(edit, setEdit)}
                    </div>
                    {casellaAdesione(edit, setEdit)}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn-cerca" onClick={() => salvaModifica(t.id)} disabled={salvando}>
                        <CheckCircle size={14} /> Salva
                      </button>
                      <button onClick={() => setEditId(null)} style={{ background: 'none', border: '1px solid #dde1e7',
                        borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#718096',
                        display: 'inline-flex', alignItems: 'center', gap: 4 }}><X size={14} /> Annulla</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, fontWeight: 600, color: '#1A1A2E' }}>
                      {t.nome}
                      {incompleto && (
                        <div style={{ fontSize: 11, fontWeight: 500, color: '#C05621', marginTop: 2,
                          display: 'flex', alignItems: 'center', gap: 4 }}>
                          <AlertTriangle size={12} /> Codice non generabile: segmenti mancanti
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1.6, fontSize: 13, color: '#718096' }}>{t.descrizione || '—'}</div>
                    <div style={{ width: 110, fontSize: 13 }}>{etichettaGrado(t.grado)}</div>
                    <div style={{ width: 70, textAlign: 'center' }}>
                      <Segmento codice={t.seg_tipo} descrizione={descr('RICORS', t.seg_tipo)} />
                    </div>
                    <div style={{ width: 70, textAlign: 'center' }}>
                      <Segmento codice={t.seg_titoli} descrizione={descr('TITOL', t.seg_titoli)} />
                    </div>
                    <div style={{ width: 80, textAlign: 'center' }}>
                      <input type="checkbox" checked={!!t.conta_come_adesione} onChange={() => toggleAdesione(t)}
                        title="Conta come adesione: permette l'origine Nuova adesione sulle schede di questo tipo" />
                    </div>
                    <div style={{ width: 190, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => {
                          setEditId(t.id); setMostraNuovo(false);
                          setEdit({ nome: t.nome, descrizione: t.descrizione || '', seg_tipo: t.seg_tipo || '', seg_titoli: t.seg_titoli || '', conta_come_adesione: !!t.conta_come_adesione, grado: t.grado || 'primo_grado' });
                          setErrore(null); setAvviso(null);
                        }}
                        style={{ background: incompleto ? '#FFF7ED' : 'none', border: `1px solid ${incompleto ? '#C05621' : '#dde1e7'}`,
                          borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12,
                          color: incompleto ? '#C05621' : '#4A5568', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Pencil size={12} /> {incompleto ? 'Completa' : 'Modifica'}
                      </button>
                      <button onClick={() => toggleAttivo(t)}
                        style={{ background: t.attivo === false ? '#EBF9F1' : '#FFF7ED',
                          border: `1px solid ${t.attivo === false ? '#276749' : '#C05621'}`,
                          color: t.attivo === false ? '#276749' : '#C05621',
                          borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                        {t.attivo === false ? 'Riattiva' : 'Disattiva'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {!tipi.length && (
            <div style={{ padding: 24, color: '#a0aec0', fontSize: 13 }}>Nessun tipo configurato.</div>
          )}
        </div>
      )}
      <div style={{ fontSize: 12, color: '#a0aec0', marginTop: 10 }}>
        Tipo e Titoli sono i segmenti del codice ricorso (es. IND + PDC per Carta Docente). I tipi disattivati restano
        sui ricorsi esistenti ma non sono proposti per i nuovi. Solo sui tipi che contano come adesione una scheda
        può avere origine "Nuova adesione".
      </div>
    </div>
  );
}
